// Client wrapper over the `suggest-pairing` edge function.
//
// Called from the RecipePairingCard's "Suggest a pairing" CTA. Sends
// the recipe's identity (title, cuisine, course, ingredients, tags)
// and an optional occasion hint to the edge function, which returns
// a single pairings block matching the shape persisted on the recipe
// row (recipe.pairings).
//
// Per the cost-discipline rule for paid AI features, this is fired
// ONLY on explicit user tap — never on recipe-page open or render.
// The result is cached to recipe.pairings so subsequent views read
// from the cached value with zero network cost.

import { supabase } from "./supabase";

/**
 * Ask Claude to draft wine / cocktail / non-alcoholic pairings for a
 * recipe.
 *
 * @param {object} args
 *   @param {object} args.recipe              — the JSONB recipe blob
 *   @param {string} [args.recipe.title]      — recipe title (≥1 of these required)
 *   @param {string} [args.recipe.cuisine]
 *   @param {string} [args.recipe.course]
 *   @param {string} [args.recipe.mealTiming]
 *   @param {Array}  [args.recipe.ingredients]
 *   @param {Array}  [args.recipe.tags]
 *   @param {string} [args.occasion]          — optional free-text
 *                                              ("date night", "weeknight")
 *
 * @returns {Promise<{ pairings?: object, error?: string }>}
 *   pairings is `{ wine, cocktail, nonAlcoholic }` on success, each
 *   carrying `{ name, style, rationale }`. cocktail / nonAlcoholic
 *   may be null if the model omitted them — the card hides those
 *   slots gracefully. On failure returns `{ error: "user-readable" }`.
 */
export async function suggestWinePairing({ recipe, occasion, allergies } = {}) {
  if (!recipe || typeof recipe !== "object") {
    return { error: "Need a recipe to pair." };
  }
  // SAFETY: pass the viewer's allergen exclusions so the model can't
  // suggest an orgeat cocktail to a tree-nut-allergic drinker.
  let allergenExclusions = [];
  if (Array.isArray(allergies) && allergies.length > 0) {
    try {
      const { allergenExclusionStrings } = await import("../data/allergens");
      allergenExclusions = allergenExclusionStrings(allergies);
    } catch { /* non-fatal — pairing proceeds without exclusions */ }
  }
  const hasTitle   = typeof recipe.title   === "string" && recipe.title.trim();
  const hasCuisine = typeof recipe.cuisine === "string" && recipe.cuisine.trim();
  if (!hasTitle && !hasCuisine) {
    return { error: "Recipe needs at least a title or cuisine to pair." };
  }

  const { data, error } = await supabase.functions.invoke("suggest-pairing", {
    body: {
      ...(allergenExclusions.length > 0 ? { allergenExclusions } : {}),
      recipe: {
        title:       recipe.title       || null,
        cuisine:     recipe.cuisine     || null,
        course:      recipe.course      || null,
        mealTiming:  recipe.mealTiming  || null,
        // Trim to first 18 ingredients server-side too, but cap here
        // so we don't waste request bytes on the long tail.
        ingredients: Array.isArray(recipe.ingredients)
          ? recipe.ingredients.slice(0, 18).map((i) => ({
              amount:       i?.amount       || null,
              item:         i?.item         || null,
              ingredientId: i?.ingredientId || null,
            }))
          : [],
        tags: Array.isArray(recipe.tags) ? recipe.tags.slice(0, 8) : [],
      },
      occasion: typeof occasion === "string" && occasion.trim()
        ? occasion.trim()
        : undefined,
    },
  });

  if (error) {
    let detail = error.message || "unknown error";
    try {
      const res = error?.context;
      if (res && typeof res.json === "function") {
        const body = await res.json();
        if (body?.error) detail = body.error;
      }
    } catch { /* fall through */ }
    return { error: detail };
  }

  // Wine pick is load-bearing; cocktail / non-alcoholic are optional.
  // The edge function already enforces this, but mirror the check
  // here so a malformed response doesn't blow up the card.
  if (!data?.pairings?.wine?.name) {
    return { error: "Response missing a wine pairing — try again." };
  }
  return { pairings: data.pairings };
}
